import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import Icon from '@mui/material/Icon';
import { Navigate, useNavigate } from 'react-router-dom';
import Person from '@mui/icons-material/Person';
import Email from '@mui/icons-material/Email';
// core components
import GridContainer from 'components/Grid/GridContainer.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardFooter from 'components/Card/CardFooter.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import GridItem from '../../components/Grid/GridItem';
import UserService from '../../services/UserService';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';

import config from '../../config/config.json';

import axios from 'axios';
import d2rsIcon from '../../assets/img/d2rsiconblock.png';
import { useMutation } from '@tanstack/react-query';
import { secondaryColor } from '../../assets/jss/material-kit-react';

interface RegisterResponse {
  session_token: string;
  has_hardcore_access: boolean;
}

interface LoginResponse {
  session_token: string;
  has_hardcore_access: boolean;
}

interface RegisterCredentials {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    padding: '0 20px',
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    width: '80vw',
    maxWidth: '80vw',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
  },
  cardHeader: {
    textAlign: 'center',
    '& h4': {
      marginTop: '0',
      marginBottom: '0',
      fontSize: '2rem',
    },
  },
  form: {
    margin: '0',
  },
  inputIconsColor: {
    color: secondaryColor + ' !important',
  },
  divider: {
    width: '100%',
    textAlign: 'center' as const,
    marginTop: '1rem',
    marginBottom: '1rem',
    position: 'relative' as const,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: '1px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    '& span': {
      backgroundColor: '#2d2d2d',
      padding: '0 1rem',
      color: 'rgba(255, 255, 255, 0.5)',
      position: 'relative',
      zIndex: 1,
    },
  },
  cardFooter: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '0.9375rem 1.875rem',
    border: 'none',
  },
  discordButton: {
    color: '#7289da !important',
    borderColor: '#7289da !important',
    '&:hover': {
      borderColor: '#FFD700 !important',
    },
  },
  socialIcons: {
    maxWidth: '24px',
    marginRight: '8px',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    color: '#7289da !important',
  },
}));

export default function RegisterPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateSessionToken } = useHeaderLinks();
  useDocumentTitle(t('navigation.register'));

  const [formData, setFormData] = useState<RegisterCredentials>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    const fieldId = id.replace('input.', '');
    setFormData((prev) => ({
      ...prev,
      [fieldId]: e.target.value,
    }));
  };

  const loginMutation = useMutation({
    mutationFn: async (credentials: { name: string; password: string }) => {
      const response = await axios.post<LoginResponse>(
        config.backend_base_url + 'login',
        credentials
      );
      return response.data;
    },
    onSuccess: async (data) => {
      localStorage.setItem('session_token', data.session_token);
      localStorage.setItem('has_hardcore_access', String(data.has_hardcore_access));
      updateSessionToken(data.session_token);

      const userService = UserService.getInstance();
      userService.clearCache();

      toast.success(t('login.success'));
      navigate('/profile', { replace: true });
    },
    onError: () => {
      toast.error(t('login.errors.failed'));
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (credentials: RegisterCredentials) => {
      if (credentials.password !== credentials.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(credentials.email)) {
        throw new Error('Invalid email format');
      }

      const response = await axios.post<RegisterResponse>(config.backend_base_url + 'register', {
        name: credentials.name,
        email: credentials.email,
        password: credentials.password,
      });
      return response.data;
    },
    onSuccess: async () => {
      // After successful registration, automatically log in
      loginMutation.mutate({
        name: formData.name,
        password: formData.password,
      });
    },
    onError: (error) => {
      if (error instanceof Error) {
        if (error.message === 'Passwords do not match') {
          toast.error(t('register.errors.passwordMismatch'));
        } else if (error.message === 'Invalid email format') {
          toast.error(t('register.errors.invalidEmail'));
        } else {
          toast.error(t('register.errors.failed'));
        }
      } else {
        toast.error(t('register.errors.failed'));
      }
    },
  });

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error(t('register.errors.emptyFields'));
      return;
    }

    registerMutation.mutate(formData);
  };

  const handleDiscordRegister = () => {
    try {
      const clientId = config.discord_client_id;
      const redirectUri = `${window.location.origin}/auth/discord/callback`;

      if (!redirectUri.startsWith('http://') && !redirectUri.startsWith('https://')) {
        throw new Error('Invalid redirect URI format');
      }

      const scope = 'identify email';
      const authUrl = `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&response_type=code&scope=${encodeURIComponent(scope)}`;

      window.location.href = authUrl;
    } catch (error) {
      console.error('Discord OAuth Error:', error);
      toast.error(t('register.errors.discordAuthFailed'));
    }
  };

  const loggedIn = localStorage.getItem('session_token');
  if (loggedIn) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <GridContainer style={{ justifyContent: 'center' }}>
              <GridItem xs={12} sm={12} md={4}>
                <Card>
                  <form className={classes.form} id="registerForm" onSubmit={handleRegister}>
                    <CardHeader color="primary" className={classes.cardHeader}>
                      <img style={{ width: '128px' }} src={d2rsIcon} alt="d2rs.io" />
                    </CardHeader>

                    <CardBody>
                      <CustomInput
                        labelText={t('register.username')}
                        id="name"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: 'text',
                          value: formData.name,
                          onChange: handleInputChange,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Person className={classes.inputIconsColor} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <CustomInput
                        labelText={t('register.email')}
                        id="email"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: 'email',
                          value: formData.email,
                          onChange: handleInputChange,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Email className={classes.inputIconsColor} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <CustomInput
                        labelText={t('register.password')}
                        id="password"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: 'password',
                          value: formData.password,
                          onChange: handleInputChange,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Icon className={classes.inputIconsColor}>lock_outline</Icon>
                            </InputAdornment>
                          ),
                          autoComplete: 'new-password',
                        }}
                      />
                      <CustomInput
                        labelText={t('register.confirmPassword')}
                        id="confirmPassword"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          type: 'password',
                          value: formData.confirmPassword,
                          onChange: handleInputChange,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Icon className={classes.inputIconsColor}>lock_outline</Icon>
                            </InputAdornment>
                          ),
                          autoComplete: 'new-password',
                        }}
                      />
                    </CardBody>

                    <CardFooter className={classes.cardFooter}>
                      <Button
                        style={{ marginBottom: '8px' }}
                        color="primary"
                        size="lg"
                        type="submit"
                        fullWidth={true}
                        form="registerForm"
                        disabled={registerMutation.isPending}
                      >
                        {registerMutation.isPending
                          ? t('common.loading.fetchingData')
                          : t('register.registerButton')}
                      </Button>
                      <Button
                        color="outlined"
                        size="lg"
                        fullWidth={true}
                        onClick={() => navigate('/login')}
                      >
                        {t('register.alreadyHaveAccount')}
                      </Button>
                      <div className={classes.divider}></div>
                      <Button
                        className={classes.discordButton}
                        color="outlined"
                        size="lg"
                        onClick={handleDiscordRegister}
                      >
                        <Icon className={classes.socialIcons}>discord</Icon>
                        {t('login.discordButton')}
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="register" helpText={t('help.register')} />
    </div>
  );
}
